import { useEffect, useRef, useState } from 'react';
import styles from './Onboarding.module.css';

const LENGTH = 6;
const RESEND_SECONDS = 60;

export default function VerifyStep({ email, onVerify, onResend, onChangeEmail }) {
  const [digits, setDigits] = useState(Array(LENGTH).fill(''));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [seconds, setSeconds] = useState(RESEND_SECONDS);
  const refs = useRef([]);

  useEffect(() => {
    if (seconds <= 0) return undefined;
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds]);

  const code = digits.join('');

  // A whole code can arrive in one change (autofill, paste), so spread it from box i.
  const setDigit = (i, raw) => {
    const value = raw.length === 2 && digits[i] ? raw.replace(digits[i], '') : raw;
    const chars = value.replace(/\D/g, '').slice(0, LENGTH - i).split('');
    const next = [...digits];
    if (chars.length === 0) next[i] = '';
    chars.forEach((c, k) => { next[i + k] = c; });
    setDigits(next);
    setError('');
    if (chars.length) refs.current[Math.min(i + chars.length, LENGTH - 1)].focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) refs.current[i - 1].focus();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (code.length < LENGTH || busy) return;
    setBusy(true);
    const message = await onVerify(code);
    if (message) {
      setError(message);
      setDigits(Array(LENGTH).fill(''));
      refs.current[0].focus();
    }
    setBusy(false);
  };

  const resend = async () => {
    setSeconds(RESEND_SECONDS);
    setError((await onResend()) || '');
  };

  return (
    <form onSubmit={submit}>
      <div className={styles.codeRow}>
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => { refs.current[i] = el; }}
            className={styles.codeBox}
            inputMode="numeric"
            autoComplete={i === 0 ? 'one-time-code' : 'off'}
            aria-label={`Digit ${i + 1}`}
            value={d}
            autoFocus={i === 0}
            onChange={(e) => setDigit(i, e.target.value)}
            onKeyDown={(e) => handleKeyDown(i, e)}
          />
        ))}
      </div>
      {error && <div className={styles.error} role="alert">{error}</div>}
      <div className={styles.verifyActions}>
        <button type="submit" className={styles.primaryButton} disabled={code.length < LENGTH || busy}>
          {busy ? 'Verifying…' : 'Verify email'}
        </button>
        {seconds > 0
          ? <span className={styles.note}>Resend code in {seconds}s</span>
          : <button type="button" className={styles.link} onClick={resend}>Resend code</button>}
        <button type="button" className={styles.link} onClick={onChangeEmail}>Change email</button>
        <span className={styles.note} style={{ margin: 0 }}>Sent to {email}</span>
      </div>
    </form>
  );
}
